import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UserProfile } from '../../../../types/types';
import { AppSyncUser } from '../../../../services/auth/types/user';
import { useWebSocketContext } from '../../../../services/websocket/context/WebSocketContext';
import { WebSocketMessage } from '../../../../services/websocket/hooks/useWebSocket';
import { createTrip } from '../../../../services/trips/api/tripsApi';
import './Plan.css';

// Google Maps Platform Extended Component Library
import '@googlemaps/extended-component-library/api_loader.js';
import '@googlemaps/extended-component-library/place_picker.js';

// Type declarations for GMP components and Google Maps
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'gmpx-api-loader': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        key?: string;
        'solution-channel'?: string;
        children?: React.ReactNode;
      };
      'gmpx-place-picker': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        placeholder?: string;
        'for-map'?: string;
        children?: React.ReactNode;
      };
    }
  }
  
  interface Window {
    google: any;
  }
}

interface PlanProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
}

interface Location {
  name: string;
  address: string;
  index: number;
}

// Agent response types
type IntentType = 
  | 'CHAT_ONLY'
  | 'START_TRIP'
  | 'COLLECT_TRIP_INFO'
  | 'CONFIRM_TRIP'
  | 'PLAN_ACTIVITIES'
  | 'SUGGEST_RECOMMENDATIONS'
  | 'CANCEL_OR_RESET';

interface TripDraft {
  title?: string;
  destinations: string[];
  dates: {
    startDate: string; // MM-DD-YYYY
    endDate: string; // MM-DD-YYYY
  } | null;
  budget: {
    amount: number;
    currency: string;
  } | null;
  description?: string;
}

interface LocationRecommendation {
  name: string;
  place_id: string;
}

// Compact, JSON-serializable view of a Google Places details object, sent to the
// agent so it can answer questions about the currently-viewed place without a
// second Places lookup. Only primitives/string arrays — no LatLng or functions.
function normalizeSelectedPlace(place: any, placeId?: string): Record<string, any> | null {
  if (!place) return null;
  const openingHours = place.opening_hours || {};
  return {
    name: place.name,
    place_id: place.place_id || placeId,
    address: place.formatted_address,
    phone: place.formatted_phone_number,
    website: place.website,
    rating: place.rating,
    user_ratings_total: place.user_ratings_total,
    price_level: place.price_level,
    types: place.types,
    opening_hours: openingHours.weekday_text, // e.g. ["Monday: 9:00 AM – 5:00 PM", ...]
  };
}

interface AgentResponse {
  intent: IntentType;
  confidence: number;
  message: string;
  trip_draft?: TripDraft;
  locations_recommendations?: LocationRecommendation[];
}

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  locations?: Location[]; // Old format, kept for backwards compatibility
  selectedLocation?: string; // Store selected location name
  // New agent response fields
  intent?: IntentType;
  confidence?: number;
  trip_draft?: TripDraft;
  locations_recommendations?: LocationRecommendation[];
  isError?: boolean;
}

interface Conversation {
  conversation_id: string;
  created_at: number;
  preview: string;
  actor: string;
}

// Supported currencies
const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'UAH', symbol: '₴', name: 'Ukrainian Hryvnia' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' }
];

// Helper function to convert MM-DD-YYYY to YYYY-MM-DD for HTML date input
const convertDateForInput = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    // MM-DD-YYYY -> YYYY-MM-DD
    return `${parts[2]}-${parts[0]}-${parts[1]}`;
  }
  return dateStr;
};

// Helper function to convert YYYY-MM-DD back to MM-DD-YYYY
const convertDateFromInput = (dateStr: string): string => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    // YYYY-MM-DD -> MM-DD-YYYY
    // parts[0] = YYYY, parts[1] = MM, parts[2] = DD
    return `${parts[1]}-${parts[2]}-${parts[0]}`;
  }
  return dateStr;
};

// Trip Draft Form Component
interface TripDraftFormProps {
  tripDraft: TripDraft;
  onConfirm?: (updatedDraft: TripDraft) => void;
  onDraftChangeRef?: React.MutableRefObject<(() => TripDraft) | null>;
  disabled?: boolean;
}

const TripDraftForm: React.FC<TripDraftFormProps> = ({ tripDraft, onConfirm, onDraftChangeRef, disabled = false }) => {
  const [destinations, setDestinations] = useState<string[]>(tripDraft.destinations || []);
  const [title, setTitle] = useState(tripDraft.title || tripDraft.destinations.join(', '));
  const [description, setDescription] = useState(tripDraft.description || '');
  const [startDate, setStartDate] = useState(tripDraft.dates ? convertDateForInput(tripDraft.dates.startDate) : '');
  const [endDate, setEndDate] = useState(tripDraft.dates ? convertDateForInput(tripDraft.dates.endDate) : '');
  const [budgetAmount, setBudgetAmount] = useState(tripDraft.budget?.amount || 0);
  const [budgetCurrency, setBudgetCurrency] = useState(tripDraft.budget?.currency || 'USD');

  // Update title when destinations change
  useEffect(() => {
    if (!tripDraft.title && destinations.length > 0) {
      setTitle(destinations.join(', '));
    }
  }, [destinations, tripDraft.title]);

  // Helper to build current draft
  const buildDraft = useCallback((): TripDraft => {
    return {
      title: title || undefined,
      destinations,
      dates: startDate && endDate ? {
        startDate: convertDateFromInput(startDate),
        endDate: convertDateFromInput(endDate)
      } : null,
      budget: budgetAmount > 0 ? {
        amount: budgetAmount,
        currency: budgetCurrency
      } : null,
      description: description || undefined
    };
  }, [title, destinations, startDate, endDate, budgetAmount, budgetCurrency, description]);

  // Expose buildDraft function via ref if provided
  useEffect(() => {
    if (onDraftChangeRef) {
      onDraftChangeRef.current = buildDraft;
    }
  }, [buildDraft, onDraftChangeRef]);

  // Sync destinations with tripDraft prop changes (when AI updates them)
  useEffect(() => {
    if (tripDraft.destinations && JSON.stringify(tripDraft.destinations) !== JSON.stringify(destinations)) {
      setDestinations(tripDraft.destinations);
    }
  }, [tripDraft.destinations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveDestination = (index: number) => {
    setDestinations(destinations.filter((_, i) => i !== index));
  };



  return (
    <div style={{
      marginTop: '16px',
      padding: '20px',
      background: '#1e293b',
      border: '2px solid #334155',
      borderRadius: '12px'
    }}>
      <h4 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#f1f5f9' }}>
        📋 Trip Details
      </h4>

      {/* Title */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#cbd5e1' }}>
          Trip Title
        </label>
          <input
            type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter trip title"
          disabled={disabled}
            style={{
            width: '100%',
            padding: '10px 12px',
            background: '#0f172a',
            border: '1px solid #475569',
              borderRadius: '8px',
              fontSize: '14px',
            color: '#f1f5f9',
            outline: 'none',
            transition: 'border-color 0.2s',
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'text'
          }}
          onFocus={(e) => !disabled && (e.target.style.borderColor = '#3b82f6')}
          onBlur={(e) => !disabled && (e.target.style.borderColor = '#475569')}
        />
        </div>

      {/* Destinations */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#cbd5e1' }}>
          Destinations
        </label>
        {destinations.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {destinations.map((dest, idx) => (
            <div
              key={idx}
              style={{
                padding: '6px 12px',
                  background: '#0f172a',
                  border: '1px solid #475569',
                borderRadius: '20px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                  gap: '8px',
                  color: '#f1f5f9'
              }}
            >
              <span>{dest}</span>
              <button
                onClick={() => handleRemoveDestination(idx)}
                disabled={disabled}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: '#ef4444',
                  fontSize: '18px',
                  lineHeight: 1,
                  padding: 0,
                  width: '20px',
                  height: '20px',
                  opacity: disabled ? 0.5 : 1
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        ) : (
          <div style={{ 
            padding: '12px', 
            background: '#0f172a', 
            border: '1px solid #475569',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#94a3b8',
            fontStyle: 'italic'
          }}>
            Chat with the AI to add destinations to your trip
          </div>
        )}
      </div>

      {/* Dates */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#cbd5e1' }}>
          Dates
        </label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #475569',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#0f172a',
                color: '#f1f5f9',
                colorScheme: 'dark',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'text'
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #475569',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#0f172a',
                color: '#f1f5f9',
                colorScheme: 'dark',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'text'
              }}
            />
          </div>
        </div>
      </div>

      {/* Budget */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#cbd5e1' }}>
          Budget
        </label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
              Amount
            </label>
            <input
              type="number"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(parseFloat(e.target.value) || 0)}
              onFocus={(e) => e.target.select()}
              min="0"
              step="1"
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #475569',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#0f172a',
                color: '#f1f5f9',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'text'
              }}
            />
          </div>
          <div style={{ width: '150px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#94a3b8' }}>
              Currency
            </label>
            <select
              value={budgetCurrency}
              onChange={(e) => setBudgetCurrency(e.target.value)}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #475569',
                borderRadius: '8px',
                fontSize: '14px',
                background: '#0f172a',
                color: '#f1f5f9',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
            >
              {SUPPORTED_CURRENCIES.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.code} - {curr.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#cbd5e1' }}>
          Description <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 'normal' }}>(optional)</span>
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description for your trip..."
          rows={4}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '10px 12px',
            background: '#0f172a',
            border: '1px solid #475569',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#f1f5f9',
            outline: 'none',
            transition: 'border-color 0.2s',
            resize: 'vertical',
            fontFamily: 'inherit',
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'text'
          }}
          onFocus={(e) => !disabled && (e.target.style.borderColor = '#3b82f6')}
          onBlur={(e) => !disabled && (e.target.style.borderColor = '#475569')}
        />
      </div>

    </div>
  );
};

// Wrapper component for CONFIRM_TRIP that includes form and confirm button below it
interface ConfirmTripFormProps {
  tripDraft: TripDraft;
  onConfirm: (updatedDraft: TripDraft) => void;
  disabled?: boolean;
}

const ConfirmTripForm: React.FC<ConfirmTripFormProps> = ({ tripDraft, onConfirm, disabled = false }) => {
  const getCurrentDraftRef = useRef<(() => TripDraft) | null>(null);

  const handleConfirm = () => {
    if (getCurrentDraftRef.current && !disabled) {
      const currentDraft = getCurrentDraftRef.current();
      onConfirm(currentDraft);
    }
  };

  return (
    <>
      <TripDraftForm
        tripDraft={tripDraft}
        onDraftChangeRef={getCurrentDraftRef}
        disabled={disabled}
      />
      <button
        onClick={handleConfirm}
        disabled={disabled}
        style={{
          marginTop: '16px',
          width: '100%',
          padding: '12px 24px',
          background: disabled ? '#6b7280' : '#10b981',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background 0.2s',
          opacity: disabled ? 0.6 : 1
        }}
        onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = '#059669')}
        onMouseLeave={(e) => !disabled && (e.currentTarget.style.background = '#10b981')}
      >
        ✓ Confirm Trip
      </button>
    </>
  );
};

const Plan: React.FC<PlanProps> = ({ user, appSyncUser }) => {
  const [tripDestination, setTripDestination] = useState('');
  const [tripActivities, setTripActivities] = useState('');
  const [tripDates, setTripDates] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<{lat: number; lng: number; address: string} | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number; lng: number} | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const userLocationMarkerRef = useRef<any>(null);
  
  // Place details state
  const [selectedPlaceDetails, setSelectedPlaceDetails] = useState<any>(null);
  const [isLoadingPlaceDetails, setIsLoadingPlaceDetails] = useState(false);
  const selectedPlaceMarkerRef = useRef<any>(null);
  // Latest normalized selected-place details, sent to the agent as context.
  const selectedPlaceRef = useRef<Record<string, any> | null>(null);
  const [lastSelectedPlaceId, setLastSelectedPlaceId] = useState<string | null>(null);
  const [photoStartIndex, setPhotoStartIndex] = useState(0);
  const placeRequestCounterRef = useRef(0);
  
  // Photo lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxPhotoIndex, setLightboxPhotoIndex] = useState(0);

  // Chat/WebSocket state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  // Latest trip draft seen in the conversation — sent back to the agent with
  // every message so add/remove/change requests are applied to the real draft.
  // Kept in a ref so send callbacks always read the current value without
  // adding chatMessages to their dependency arrays.
  const latestTripDraftRef = useRef<TripDraft | null>(null);
  useEffect(() => {
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const draft = chatMessages[i].trip_draft;
      if (draft) {
        latestTripDraftRef.current = draft;
        return;
      }
    }
  }, [chatMessages]);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chatMessagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Conversation ID is provided by the agent in the first response
  // Start with null, and update it when we receive the first response from the agent
  const [conversationId, setConversationId] = useState<string | null>(null);
  
  // Test input for conversation_id
  const [testConversationId, setTestConversationId] = useState('');
  
  // Conversations history state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  
  // Track which messages have had their trips created (to disable the form)
  const [createdTripMessageIds, setCreatedTripMessageIds] = useState<Set<string>>(new Set());
  const [conversationsError, setConversationsError] = useState<string | null>(null);
  const [isConversationsDropdownOpen, setIsConversationsDropdownOpen] = useState(false);
  
  // Use existing WebSocket connection from context
  const {
    isConnected,
    isConnecting,
    connectionId,
    sendMessage: sendWebSocketMessage,
    subscribe,
    error: wsError
  } = useWebSocketContext();

  // Parse locations from response text
  // Format 1: "1. **Location Name** - Address: ... - Rating: ... - User Ratings Total: ..."
  // Format 2: "1. Location Name, located at Address, United Kingdom, with a rating of X."
  const parseLocationsFromText = useCallback((text: string): Location[] => {
    const locations: Location[] = [];
    
    // Try Format 1: "1. **Location Name** - Address: ... - Rating: ..."
    // Example: "1. **Estratto coffee** - Address: 336 W Pender St, Vancouver, BC V6B 1T1, Canada - Rating: 5 - User Ratings Total: 238"
    const format1Pattern = /(\d+)\.\s+\*\*([^*]+)\*\*\s+-\s+Address:\s+([^-]+?)(?:\s+-\s+Rating:|$)/g;
    let match;
    
    while ((match = format1Pattern.exec(text)) !== null) {
      const locationIndex = parseInt(match[1]) || locations.length + 1;
      const locationName = match[2].trim();
      let address = match[3].trim();
      
      // Clean up address - remove trailing commas
      address = address.replace(/,\s*$/, '').trim();
      
      locations.push({
        name: locationName,
        address: address,
        index: locationIndex
      });
    }
    
    // If Format 1 didn't match anything, try Format 2: "1. Location Name, located at Address..."
    if (locations.length === 0) {
      // Match pattern: "1. Location Name, located at Address..."
      // Example: "1. Castiglione, located at 17 Whitcomb St., London WC2H 7HA, United Kingdom, with a rating of 5."
      const format2Pattern = /(\d+)\.\s+([^,]+?),\s+located at\s+([^,]+(?:,\s+[^,]+)*?)(?:,\s+United Kingdom)?(?:,\s+with a rating of[^.\n]*)?\./g;
      
      while ((match = format2Pattern.exec(text)) !== null) {
        const locationIndex = parseInt(match[1]) || locations.length + 1;
        const locationName = match[2].trim();
        let address = match[3].trim();
        
        // Clean up address - remove "United Kingdom" and rating info if they were captured
        address = address.replace(/,\s*United Kingdom.*$/i, '').trim();
        
        locations.push({
          name: locationName,
          address: address,
          index: locationIndex
        });
      }
    }
    
    console.log('📍 Parsed locations from text:', locations.length, 'locations found');
    locations.forEach((loc, i) => {
      console.log(`  ${i + 1}. ${loc.name} - ${loc.address}`);
    });
    
    return locations;
  }, []);

  // Parse agent response (JSON or error message)
  // Handles both direct format: { "intent": "...", "message": "..." }
  // and nested format: { "response": { "intent": "...", "message": "..." } }
  const parseAgentResponse = useCallback((text: string): { response: AgentResponse | null; isError: boolean } => {
    if (!text || typeof text !== 'string') {
      return { response: null, isError: false };
    }

    const trimmed = text.trim();
    
    // Check if it's a JSON response
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed) as any;
        
        // Check if response is nested in a "response" object
        let agentResponse: AgentResponse | null = null;
        if (parsed.response && parsed.response.intent) {
          // Nested format: { "response": { "intent": "...", ... } }
          agentResponse = parsed.response as AgentResponse;
        } else if (parsed.intent) {
          // Direct format: { "intent": "...", ... }
          agentResponse = parsed as AgentResponse;
        }
        
        // Validate required fields
        if (agentResponse && agentResponse.intent && agentResponse.message !== undefined && agentResponse.confidence !== undefined) {
          return { response: agentResponse, isError: false };
        } else {
          console.warn('⚠️ Invalid agent response format - missing required fields:', parsed);
          return { response: null, isError: false };
        }
      } catch (error) {
        // Not valid JSON, treat as plain text message
        console.warn('⚠️ Failed to parse JSON response:', error);
        return { response: null, isError: false };
      }
    }

    // Not JSON, treat as plain text
    return { response: null, isError: false };
  }, []);

  // Ref to store the fetch function (will be set after map is initialized)
  const fetchPlaceDetailsRef = useRef<((placeId: string, placeName: string) => void) | null>(null);

  // Handle location recommendation selection (for SUGGEST_RECOMMENDATIONS intent)
  const handleLocationRecommendationSelect = useCallback(async (locationName: string, messageId: string, placeId?: string, location?: LocationRecommendation) => {
    console.log('🎯 Location recommendation selected:', locationName);
    console.log('📋 Received parameters:', {
      locationName,
      messageId,
      placeId,
      location,
      placeIdType: typeof placeId,
      placeIdFromLocation: location?.place_id,
      placeIdMatch: placeId === location?.place_id
    });
    
    // Clear previous place details first to prevent showing wrong place
    setSelectedPlaceDetails(null);
    
    // Reset photo carousel to start
    setPhotoStartIndex(0);
    
    // Clear last selected place_id (will be set on successful fetch)
    setLastSelectedPlaceId(null);
    
    // Fetch and display place details if place_id is available
    if (placeId && location) {
      console.log('✅ placeId and location are both truthy');
      console.log('🔄 Place ID to fetch:', placeId);
      console.log('🔄 Location object:', JSON.stringify(location, null, 2));
      console.log('🔍 Google Maps status:', {
        hasGoogle: !!(window as any).google,
        hasMaps: !!(window as any).google?.maps,
        hasPlaces: !!(window as any).google?.maps?.places,
        hasPlacesService: !!(window as any).google?.maps?.places?.PlacesService
      });
      
      // Set immediate feedback - show we're loading
      setSelectedPlaceDetails({
        name: locationName,
        formatted_address: 'Loading details...',
        _isFallback: true,
        _isLoading: true
      });
      setIsLoadingPlaceDetails(false); // Show the card immediately, not the spinner
      
      if (fetchPlaceDetailsRef.current) {
        fetchPlaceDetailsRef.current(placeId, locationName);
      } else {
        console.warn('⚠️ fetchPlaceDetailsRef not initialized yet');
        setSelectedPlaceDetails({
          name: locationName,
          formatted_address: 'Error: Map not ready',
          _isFallback: true
        });
      }
    } else {
      console.warn('⚠️ No place_id available for:', locationName);
      console.log('Debug - placeId:', placeId, 'location:', location);
      
      // Show feedback even if no place_id
      setSelectedPlaceDetails({
        name: locationName,
        formatted_address: 'No place ID available',
        _isFallback: true
      });
      setIsLoadingPlaceDetails(false);
    }
    
    // Use test conversation_id if provided (for testing), otherwise use the one from agent
    // Note: Location selections typically happen after agent responses, so conversationId should exist
    const convId = testConversationId.trim() || conversationId || undefined;
    
    // Log conversation ID usage (undefined is OK - backend will handle it)
    if (convId) {
      console.log('💬 Sending location recommendation selection with conversation ID:', convId);
    } else {
      console.log('💬 Sending location recommendation selection without conversation ID (backend will create one)');
    }
    
    // Send message to agent: "Selected {{location}}"
    const selectionMessage = `Selected ${locationName}`;
    
    // Add user message showing the selection
    const userMessage: ChatMessage = {
      id: `user-selection-${Date.now()}`,
      message: selectionMessage,
      isUser: true,
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    
    // Send message to agent
    try {
      // Prepare additional data: user_location and user_time
      const additionalData: { user_location?: { lat: string; lng: string }; user_time?: string; trip_draft?: TripDraft; selected_place?: Record<string, any> } = {};
      
      // Add user_location if available (convert numbers to strings)
      if (userLocation) {
        additionalData.user_location = {
          lat: userLocation.lat.toString(),
          lng: userLocation.lng.toString()
        };
      }
      
      // Add user_time as ISO string (current device time)
      additionalData.user_time = new Date().toISOString();

      // Include the current trip draft so the agent can modify it in place
      if (latestTripDraftRef.current) {
        additionalData.trip_draft = latestTripDraftRef.current;
      }
      
      await sendWebSocketMessage(selectionMessage, convId, additionalData);
      
      // Add loading indicator for response
      const loadingMessage: ChatMessage = {
        id: `loading-${Date.now()}`,
        message: '',
        isUser: false,
        timestamp: new Date(),
        isLoading: true
      };
      setChatMessages(prev => [...prev, loadingMessage]);
      setIsWaitingForResponse(true);
      
      // Set up timeout for 60 seconds (1 minute)
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      
      responseTimeoutRef.current = setTimeout(() => {
        console.warn('⏱️ No response received after 60 seconds (location recommendation selection), timing out...');
        
        setChatMessages(prev => {
          const updated = [...prev];
          const lastMessageIndex = updated.length - 1;
          
          if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
            updated[lastMessageIndex] = {
              ...updated[lastMessageIndex],
              message: 'Error: No response received from the agent. Please try again.',
              isLoading: false
            };
          } else {
            updated.push({
              id: `timeout-error-${Date.now()}`,
              message: 'Error: No response received from the agent. Please try again.',
              isUser: false,
              timestamp: new Date(),
              isLoading: false
            });
          }
          return updated;
        });
        
        setIsWaitingForResponse(false);
        responseTimeoutRef.current = null;
        
        setTimeout(() => {
          const inputElement = inputRefs.current['current-input'];
          if (inputElement) {
            inputElement.focus();
          }
          }, 100);
      }, 60000); // 1 minute
      
      // Set up timeout for 60 seconds (1 minute)
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      
      responseTimeoutRef.current = setTimeout(() => {
        console.warn('⏱️ No response received after 60 seconds, timing out...');
        
        setChatMessages(prev => {
          const updated = [...prev];
          const lastMessageIndex = updated.length - 1;
          
          if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
            updated[lastMessageIndex] = {
              ...updated[lastMessageIndex],
              message: 'Error: No response received from the agent. Please try again.',
              isLoading: false
            };
          } else {
            updated.push({
              id: `timeout-error-${Date.now()}`,
              message: 'Error: No response received from the agent. Please try again.',
              isUser: false,
              timestamp: new Date(),
              isLoading: false
            });
          }
          return updated;
        });
        
        setIsWaitingForResponse(false);
        responseTimeoutRef.current = null;
        
        setTimeout(() => {
          const inputElement = inputRefs.current['current-input'];
          if (inputElement) {
            inputElement.focus();
          }
          }, 100);
        }, 60000); // 1 minute
    } catch (error: any) {
      console.error('❌ Failed to send location recommendation selection:', error);
      
      // Clear timeout since we got an error
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      setIsWaitingForResponse(false);
    }
  }, [testConversationId, conversationId, sendWebSocketMessage, userLocation]);
  
  // Note: fetchAndDisplayPlaceDetails is called directly, not included in deps to avoid circular dependency

  // Handle location selection (old format, kept for backwards compatibility)
  const handleLocationSelect = useCallback(async (locationName: string, messageId: string) => {
    console.log('📍 Location selected:', locationName);
    
    // Update the message to show selected location
    setChatMessages(prev => {
      return prev.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            selectedLocation: locationName
          };
        }
        return msg;
      });
    });
    
    // Send message to agent: "Selected {{location}}"
    const selectionMessage = `Selected ${locationName}`;
    
    // Use test conversation_id if provided (for testing), otherwise use the one from agent
    // Note: Location selections typically happen after agent responses, so conversationId should exist
    const convId = testConversationId.trim() || conversationId || undefined;
    
    // Log conversation ID usage (undefined is OK - backend will handle it)
    if (convId) {
      console.log('💬 Sending location selection with conversation ID:', convId);
    } else {
      console.log('💬 Sending location selection without conversation ID (backend will create one)');
    }
    
    // Add user message showing the selection
    const userMessage: ChatMessage = {
      id: `user-selection-${Date.now()}`,
      message: selectionMessage,
      isUser: true,
      timestamp: new Date()
    };
    
      setChatMessages(prev => [...prev, userMessage]);
      
      // Send message to agent
      try {
        // Prepare additional data: user_location and user_time
        const additionalData: { user_location?: { lat: string; lng: string }; user_time?: string; trip_draft?: TripDraft; selected_place?: Record<string, any> } = {};
        
        // Add user_location if available (convert numbers to strings)
        if (userLocation) {
          additionalData.user_location = {
            lat: userLocation.lat.toString(),
            lng: userLocation.lng.toString()
          };
        }
        
        // Add user_time as ISO string (current device time)
        additionalData.user_time = new Date().toISOString();

      // Include the current trip draft so the agent can modify it in place
      if (latestTripDraftRef.current) {
        additionalData.trip_draft = latestTripDraftRef.current;
      }
        
        await sendWebSocketMessage(selectionMessage, convId, additionalData);
        
        // Add loading indicator for response
        const loadingMessage: ChatMessage = {
          id: `loading-${Date.now()}`,
          message: '',
          isUser: false,
          timestamp: new Date(),
          isLoading: true
        };
        setChatMessages(prev => [...prev, loadingMessage]);
        setIsWaitingForResponse(true);
        
        // Set up timeout for 60 seconds (1 minute)
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
        }
        
        responseTimeoutRef.current = setTimeout(() => {
          console.warn('⏱️ No response received after 20 seconds (location selection), timing out...');
          
          setChatMessages(prev => {
            const updated = [...prev];
            const lastMessageIndex = updated.length - 1;
            
            if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
              updated[lastMessageIndex] = {
                ...updated[lastMessageIndex],
                message: 'Error: No response received from the agent. Please try again.',
                isLoading: false
              };
            } else {
              updated.push({
                id: `timeout-error-${Date.now()}`,
                message: 'Error: No response received from the agent. Please try again.',
                isUser: false,
                timestamp: new Date(),
                isLoading: false
              });
            }
            return updated;
          });
          
          setIsWaitingForResponse(false);
          responseTimeoutRef.current = null;
          
          setTimeout(() => {
            const inputElement = inputRefs.current['current-input'];
            if (inputElement) {
              inputElement.focus();
            }
          }, 100);
        }, 60000); // 1 minute
      
      // Set up timeout for 60 seconds (1 minute)
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
      
      responseTimeoutRef.current = setTimeout(() => {
        console.warn('⏱️ No response received after 60 seconds, timing out...');
        
        setChatMessages(prev => {
          const updated = [...prev];
          const lastMessageIndex = updated.length - 1;
          
          if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
            updated[lastMessageIndex] = {
              ...updated[lastMessageIndex],
              message: 'Error: No response received from the agent. Please try again.',
              isLoading: false
            };
          } else {
            updated.push({
              id: `timeout-error-${Date.now()}`,
              message: 'Error: No response received from the agent. Please try again.',
              isUser: false,
              timestamp: new Date(),
              isLoading: false
            });
          }
          return updated;
        });
        
        setIsWaitingForResponse(false);
        responseTimeoutRef.current = null;
        
        setTimeout(() => {
          const inputElement = inputRefs.current['current-input'];
          if (inputElement) {
            inputElement.focus();
          }
          }, 100);
        }, 60000); // 1 minute
    } catch (error: any) {
      console.error('❌ Failed to send location selection:', error);
      
      // Clear timeout since we got an error
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      setIsWaitingForResponse(false);
    }
  }, [testConversationId, conversationId, sendWebSocketMessage, userLocation]);

  // Handle incoming WebSocket messages
  // Agent responses can be JSON (AgentResponse format) or error messages
  const handleWebSocketMessage = useCallback((data: WebSocketMessage) => {
    console.log('📨 Received WebSocket message in Plan component:', data);
    console.log('📨 Full message object keys:', Object.keys(data));
    console.log('📨 Message fields:', { 
      status: (data as any).status, 
      text: (data as any).text, 
      message: data.message,
      conversation_id: data.conversation_id,
      message_id: (data as any).message_id
    });
    
    // Extract conversation_id from agent response and store it for future messages
    if (data.conversation_id) {
      const receivedConvId = data.conversation_id;
      console.log('💬 Conversation ID received from agent:', receivedConvId);
      
      // If we don't have a conversation ID yet, or it's different, update it
      if (!conversationId) {
        console.log('🆕 Setting conversation ID from agent response:', receivedConvId);
        setConversationId(receivedConvId);
      } else if (conversationId !== receivedConvId) {
        console.warn('⚠️ Conversation ID changed! Old:', conversationId, 'New:', receivedConvId);
        setConversationId(receivedConvId);
      } else {
        console.log('✅ Conversation ID matches - successfully reused:', conversationId);
      }
    }
    
    // Handle different response types according to docs
    // Check for fields in multiple possible locations
    const messageStatus = (data as any).status;
    // Handle both formats: direct text field or nested in response object
    // Also check for nested response.response structure (when agent returns { "response": { ... } })
    const messageText = (data as any).text || 
                       (data as any).body?.text || 
                       (data as any).response?.text ||
                       (data as any).response?.response?.message ||
                       (data as any).response?.message;
    const hasError = !!(data as any).error || 
                    !!(data as any).body?.error || 
                    !!(data as any).response?.error ||
                    !!(data as any).response?.response?.error;
    
    console.log('🔍 Checking message status:', messageStatus, 'text:', !!messageText, 'text value:', messageText, 'error:', hasError);
    console.log('🔍 Status check:', messageStatus === 'complete', 'Text check:', !!messageText, 'Combined:', messageStatus === 'complete' && !!messageText);
    console.log('🔍 Full data object:', JSON.stringify(data, null, 2));
    
    if (messageStatus === 'processing') {
      // Processing status - agent is working on the response
      console.log('⏳ Agent is processing...');
      // Keep loading indicator active
      return;
    } else if (messageStatus === 'complete') {
      // Complete response - check if we have a nested response object first
      let agentResponse: AgentResponse | null = null;
      let responseText: string = '';
      let isError = false;
      
      // Check if data.response is already a parsed object (not a string)
      if ((data as any).response && typeof (data as any).response === 'object' && (data as any).response.intent) {
        // Direct response object structure: { response: { intent: "...", ... } }
        console.log('✅ Found nested response object in data.response');
        agentResponse = (data as any).response as AgentResponse;
        responseText = agentResponse.message || messageText || '';
      } else if (messageText) {
        // Fallback to parsing text field as JSON
        responseText = messageText;
        const parsed = parseAgentResponse(responseText);
        agentResponse = parsed.response;
        isError = parsed.isError;
      } else {
        // No text or response found
        console.warn('⚠️ No response text or object found in message');
        return;
      }
      
      console.log('✅ Received complete response!');
      console.log('✅ Response text:', responseText);
      console.log('✅ Agent response:', agentResponse);
      
      setChatMessages(prev => {
        console.log('📝 Current chat messages before update:', prev.length);
        console.log('📝 Messages:', prev.map((m, i) => ({ 
          index: i,
          id: m.id, 
          isLoading: m.isLoading, 
          hasText: !!m.message,
          preview: m.message?.substring(0, 30) || 'empty'
        })));
        
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        const hasLoadingMessage = lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading;
        
        console.log('📝 Last message index:', lastMessageIndex);
        console.log('📝 Has loading message:', hasLoadingMessage);
        
        // Build message object
        const messageData: Partial<ChatMessage> = {
          message: agentResponse ? agentResponse.message : responseText,
          isLoading: false,
          isError: isError || false
        };
        
        // If it's a valid agent response, include agent-specific fields
        if (agentResponse) {
          messageData.intent = agentResponse.intent;
          messageData.confidence = agentResponse.confidence;
          messageData.trip_draft = agentResponse.trip_draft;
          messageData.locations_recommendations = agentResponse.locations_recommendations;
        } else {
          // Fallback to old location parsing for backwards compatibility
          const locations = parseLocationsFromText(responseText);
          if (locations.length > 0) {
            messageData.locations = locations;
          }
        }
        
        if (hasLoadingMessage) {
          // Update the loading message with the actual response
          console.log('✅ Updating loading message #' + lastMessageIndex + ' with response text');
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            ...messageData
          } as ChatMessage;
        } else {
          // If no loading message, add a new message
          console.log('✅ Adding new assistant message (no loading message found, adding new one)');
          const newMessage: ChatMessage = {
            id: `assistant-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            isUser: false,
            timestamp: new Date(),
            ...messageData
          } as ChatMessage;
          console.log('✅ New message created:', { id: newMessage.id, messageLength: newMessage.message.length, intent: newMessage.intent });
          updated.push(newMessage);
        }
        
        console.log('📝 Updated chat messages after update:', updated.length);
        console.log('📝 Last message preview:', updated[updated.length - 1]?.message?.substring(0, 50) + '...');
        console.log('📝 Last message full object:', { 
          id: updated[updated.length - 1]?.id,
          isLoading: updated[updated.length - 1]?.isLoading,
          messageLength: updated[updated.length - 1]?.message?.length,
          intent: updated[updated.length - 1]?.intent
        });
        return updated;
      });
      
      // Clear timeout since we received a response
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      setIsWaitingForResponse(false);
      console.log('✅ Set isWaitingForResponse to false');
      
      // Focus on input after response is received
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    } else if ((data as any).error) {
      // Error response
      const errorMessage = (data as any).error || 'An error occurred';
      console.error('❌ WebSocket error detected:', errorMessage);
      
      setChatMessages(prev => {
        console.log('❌ Error handler: Updating chat messages, current count:', prev.length);
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        const hasLoadingMessage = lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading;
        
        console.log('❌ Error handler: Has loading message:', hasLoadingMessage, 'Last index:', lastMessageIndex);
        
        if (hasLoadingMessage) {
          console.log('❌ Error handler: Updating loading message with error');
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            message: `Error: ${errorMessage}`,
            isLoading: false
          };
        } else {
          console.log('❌ Error handler: Adding new error message');
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            message: `Error: ${errorMessage}`,
            isUser: false,
            timestamp: new Date(),
            isLoading: false
          };
          console.log('❌ Error handler: Created error message:', errorMsg);
          updated.push(errorMsg);
        }
        
        console.log('❌ Error handler: Updated chat messages count:', updated.length);
        console.log('❌ Error handler: Last message:', updated[updated.length - 1]?.message);
        return updated;
      });
      // Clear timeout since we received a response (error)
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      setIsWaitingForResponse(false);
      console.log('❌ Error handler: Set isWaitingForResponse to false');
      
      // Focus on input after error
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    } else if (data.message) {
      // Fallback: if message field is present (for compatibility)
      const responseText = data.message || '';
      const { response: agentResponse, isError } = parseAgentResponse(responseText);
      
      setChatMessages(prev => {
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        
        const messageData: Partial<ChatMessage> = {
          message: agentResponse ? agentResponse.message : responseText,
          isLoading: false,
          isError: isError || false
        };
        
        if (agentResponse) {
          messageData.intent = agentResponse.intent;
          messageData.confidence = agentResponse.confidence;
          messageData.trip_draft = agentResponse.trip_draft;
          messageData.locations_recommendations = agentResponse.locations_recommendations;
        } else {
          const locations = parseLocationsFromText(responseText);
          if (locations.length > 0) {
            messageData.locations = locations;
          }
        }
        
        if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            ...messageData
          } as ChatMessage;
        } else {
          // If no loading message, add new message
          updated.push({
            id: `assistant-${Date.now()}`,
            isUser: false,
            timestamp: new Date(),
            ...messageData
          } as ChatMessage);
        }
        return updated;
      });
      // Clear timeout since we received a response
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      setIsWaitingForResponse(false);
      
      // Focus on input after response is received
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    } else if (messageText) {
      // Catch-all: if text field is present (even without status), display it
      console.log('✅ Received message with text field (fallback handler)');
      console.log('✅ Status was:', messageStatus, 'but text is present');
      const responseText = messageText || '';
      
      console.log('✅ Fallback: Adding message with text, length:', responseText.length);
      const { response: agentResponse, isError } = parseAgentResponse(responseText);
      
      setChatMessages(prev => {
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        
        const messageData: Partial<ChatMessage> = {
          message: agentResponse ? agentResponse.message : responseText,
          isLoading: false,
          isError: isError || false
        };
        
        if (agentResponse) {
          messageData.intent = agentResponse.intent;
          messageData.confidence = agentResponse.confidence;
          messageData.trip_draft = agentResponse.trip_draft;
          messageData.locations_recommendations = agentResponse.locations_recommendations;
        } else {
          const locations = parseLocationsFromText(responseText);
          if (locations.length > 0) {
            messageData.locations = locations;
          }
        }
        
        if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
          console.log('✅ Fallback: Updating loading message');
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            ...messageData
          } as ChatMessage;
        } else {
          console.log('✅ Fallback: Adding new assistant message');
          updated.push({
            id: `assistant-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            isUser: false,
            timestamp: new Date(),
            ...messageData
          } as ChatMessage);
        }
        
        console.log('✅ Fallback: Updated messages count:', updated.length);
        return updated;
      });
      
      setIsWaitingForResponse(false);
      
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    } else {
      // Log unhandled message format for debugging
      console.warn('⚠️ Unhandled message format:', data);
      console.warn('⚠️ Available fields:', Object.keys(data));
      console.warn('⚠️ Message status:', messageStatus);
      console.warn('⚠️ Has text:', !!messageText, 'text value:', messageText);
      console.warn('⚠️ Has error:', !!hasError);
      console.warn('⚠️ Has message:', !!data.message);
      
      // Final fallback: if we have any text at all (even if status doesn't match), try to display it
      if (messageText && typeof messageText === 'string' && messageText.trim()) {
        console.log('🆘 Final fallback: Attempting to display message with text');
        const responseText = messageText.trim();
        const { response: agentResponse, isError } = parseAgentResponse(responseText);
        
        setChatMessages(prev => {
          const updated = [...prev];
          const lastMessageIndex = updated.length - 1;
          
          const messageData: Partial<ChatMessage> = {
            message: agentResponse ? agentResponse.message : responseText,
            isLoading: false,
            isError: isError || false
          };
          
          if (agentResponse) {
            messageData.intent = agentResponse.intent;
            messageData.confidence = agentResponse.confidence;
            messageData.trip_draft = agentResponse.trip_draft;
            messageData.locations_recommendations = agentResponse.locations_recommendations;
          } else {
            const locations = parseLocationsFromText(responseText);
            if (locations.length > 0) {
              messageData.locations = locations;
            }
          }
          
          if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
            updated[lastMessageIndex] = {
              ...updated[lastMessageIndex],
              ...messageData
            } as ChatMessage;
          } else {
            updated.push({
              id: `assistant-fallback-${Date.now()}`,
              isUser: false,
              timestamp: new Date(),
              ...messageData
            } as ChatMessage);
          }
          return updated;
        });
        
        setIsWaitingForResponse(false);
      }
    }
  }, [parseLocationsFromText, conversationId, testConversationId]);

  // Subscribe to WebSocket messages
  // Use ref to avoid re-subscribing when handler changes
  const handleWebSocketMessageRef = useRef(handleWebSocketMessage);
  useEffect(() => {
    handleWebSocketMessageRef.current = handleWebSocketMessage;
  }, [handleWebSocketMessage]);

  useEffect(() => {
    if (subscribe) {
      console.log('🔔 Subscribing to WebSocket messages...');
      const unsubscribe = subscribe((message) => {
        console.log('🔔 Subscription callback called with message:', message);
        handleWebSocketMessageRef.current(message);
      });
      console.log('✅ Successfully subscribed to WebSocket messages');
      return () => {
        console.log('🔕 Unsubscribing from WebSocket messages');
        unsubscribe();
      };
    } else {
      console.warn('⚠️ Subscribe function is not available');
    }
  }, [subscribe]); // Only re-subscribe if subscribe function changes

  // Debug connection status
  useEffect(() => {
    console.log('🔍 WebSocket connection status in Plan:', {
      isConnected,
      isConnecting,
      conversationId: testConversationId.trim() || conversationId,
      connectionId,
      wsError
    });
  }, [isConnected, isConnecting, conversationId, testConversationId, connectionId, wsError]);

  // Get Google Maps API key from environment variables
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = !!googleMapsApiKey;
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<any>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  // Load Google Maps JavaScript API with Places library
  useEffect(() => {
    if (!hasApiKey) {
      console.warn('⚠️ Google Maps API key not configured');
      return;
    }

    console.log('🔑 Google Maps API Key present:', googleMapsApiKey?.substring(0, 10) + '...');

    // Check if already loaded - just check for base maps API, not places
    if (window.google?.maps?.Map) {
      console.log('✅ Google Maps API already loaded');
      setIsGoogleMapsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('⏳ Google Maps API script already in DOM, waiting for load...');
      console.log('📍 Script src:', existingScript.getAttribute('src'));
      
      // Wait for it to load - just need the Map API, Places is optional
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds
      const checkLoaded = setInterval(() => {
        attempts++;
        
        // Check if Maps API is loaded (Places is optional)
        const hasMap = !!(window as any).google?.maps?.Map;
        const hasPlaces = !!(window as any).google?.maps?.places?.PlacesService;
        
        console.log(`🔍 Attempt ${attempts}: hasMap=${hasMap}, hasPlaces=${hasPlaces}`);
        
        if (hasMap) {
          console.log('✅ Google Maps API loaded after', attempts * 100, 'ms');
          if (hasPlaces) {
            console.log('✅ Places API also available');
          } else {
            console.warn('⚠️ Places API not available yet, will try to load later');
            console.log('Available in google.maps:', Object.keys((window as any).google?.maps || {}));
          }
          setIsGoogleMapsLoaded(true);
          clearInterval(checkLoaded);
          return;
        }
        
        // Timeout after max attempts
        if (attempts >= maxAttempts) {
          console.error('❌ Timeout waiting for Google Maps API to load');
          console.error('💡 Status:', {
            hasGoogle: !!(window as any).google,
            hasMap,
            hasPlaces,
            mapsKeys: (window as any).google?.maps ? Object.keys((window as any).google.maps) : [],
            placesKeys: (window as any).google?.maps?.places ? Object.keys((window as any).google.maps.places) : []
          });
          clearInterval(checkLoaded);
        }
      }, 100);
      
      return () => clearInterval(checkLoaded);
    }

    console.log('📡 Creating Google Maps API script tag...');
    
    // Create and append script tag
    const script = document.createElement('script');
    // Use v=weekly for stable version, load places library
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places&v=weekly`;
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    
    console.log('🌐 Script URL:', scriptUrl.replace(googleMapsApiKey || '', 'API_KEY_HIDDEN'));
    
    script.onload = () => {
      console.log('✅ Google Maps API script onload fired');
      
      // Wait a moment for libraries to initialize
      const checkInterval = setInterval(() => {
        const hasMap = !!(window as any).google?.maps?.Map;
        const hasPlaces = !!(window as any).google?.maps?.places?.PlacesService;
        
        console.log('🔍 Checking Google Maps libraries:', { hasMap, hasPlaces });
        console.log('🔍 Available in google.maps:', Object.keys((window as any).google?.maps || {}));
        
        // Only require Map, Places is optional
        if (hasMap) {
          console.log('✅ Google Maps API fully loaded');
          if (hasPlaces) {
            console.log('✅ Places API also available');
          } else {
            console.warn('⚠️ Places API not loaded - check if Places API is enabled in Google Cloud Console');
            console.log('📝 To enable: https://console.cloud.google.com/apis/library/places-backend.googleapis.com');
          }
          setIsGoogleMapsLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);
      
      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        const hasMap = !!(window as any).google?.maps?.Map;
        if (!hasMap) {
          console.error('❌ Map API failed to load within 5 seconds');
          console.log('🔍 Available:', {
            hasGoogle: !!window.google,
            hasMaps: !!(window as any).google?.maps,
            mapsKeys: window.google?.maps ? Object.keys(window.google.maps) : []
          });
        }
      }, 5000);
    };
    
    script.onerror = (error) => {
      console.error('❌ Failed to load Google Maps API script');
      console.error('Error details:', error);
      console.error('💡 Possible causes:');
      console.error('   1. Invalid API key');
      console.error('   2. Network connectivity issues');
      console.error('   3. CORS or security restrictions');
      console.error('   4. API not enabled in Google Cloud Console');
    };
    
    document.head.appendChild(script);
    console.log('📤 Script tag appended to document head');
    
    // Cleanup function
    return () => {
      // Don't remove the script as it might be needed for other components
      // script.remove();
    };
  }, [hasApiKey, googleMapsApiKey]);

  // Request user's location automatically on page load
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    console.log('📍 Requesting user location on page load...');

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        console.log('✅ Location obtained:', location);
        setUserLocation(location);
        setLocationError(null);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        setLocationError(errorMessage);
        console.warn('⚠️ Geolocation error:', errorMessage);
      },
      options
    );
  }, []); // Run once on mount

  // Update map when user location is obtained and map is ready
  useEffect(() => {
    if (userLocation && map && window.google?.maps?.Marker) {
      console.log('📍 Updating map with user location:', userLocation);
      map.setCenter(userLocation);
      map.setZoom(13);
      
      // Add or update user location marker (using standard Marker)
      if (userLocationMarkerRef.current) {
        // Update existing marker position
        userLocationMarkerRef.current.setPosition(userLocation);
        console.log('✅ Updated existing user location marker');
      } else {
        // Create new marker
        userLocationMarkerRef.current = new window.google.maps.Marker({
          map: map,
          position: userLocation,
          title: 'Your Location',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#4285F4',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2
          }
        });
        console.log('✅ Created new user location marker');
      }
    }
  }, [userLocation, map]);

  // Initialize Google Maps map instance
  useEffect(() => {
    if (!hasApiKey || !mapRef.current || !isGoogleMapsLoaded) {
      console.log('⏸️ Waiting for:', { hasApiKey, hasMapRef: !!mapRef.current, isGoogleMapsLoaded });
      return;
    }

    console.log('🗺️ Initializing Google Maps...');

    // Use a ref to store the interval ID to avoid closure issues
    let checkInterval: NodeJS.Timeout | null = null;

    // Function to initialize the map once Google Maps API is loaded
    const initializeMap = () => {
      // Check if Google Maps API is fully loaded and Map constructor is available
      // Check what's available
      const hasBaseMaps = window.google && window.google.maps && typeof window.google.maps.Map === 'function';
      const hasMarker = window.google?.maps?.marker && typeof window.google.maps.marker.AdvancedMarkerElement === 'function';
      const hasStandardMarker = window.google?.maps?.Marker;
      const hasPlaces = window.google?.maps?.places && typeof window.google.maps.places.PlacesService === 'function';
      
      console.log('🔍 API Check:', { 
        hasBaseMaps, 
        hasMarker, 
        hasStandardMarker,
        hasPlaces, 
        hasMapRef: !!mapRef.current, 
        hasMap: !!map,
        isGoogleMapsLoaded 
      });
      
      // Only require base maps - markers and places are optional
      if (
        hasBaseMaps &&
        mapRef.current && 
        !map
      ) {
        console.log('✅ Google Maps base API available, creating map instance...');
        if (!hasMarker && !hasStandardMarker) {
          console.warn('⚠️ Marker library not loaded yet, markers will be unavailable');
        }
        if (!hasPlaces) {
          console.warn('⚠️ Places library not loaded yet, place details will be unavailable');
        }
        try {
          // Determine map center: selectedLocation > userLocation > default (NYC)
          let mapCenter = { lat: 40.749933, lng: -73.98633 }; // Default to NYC
          if (selectedLocation) {
            mapCenter = { lat: selectedLocation.lat, lng: selectedLocation.lng };
          } else if (userLocation) {
            mapCenter = userLocation;
          }

          const mapInstance = new window.google.maps.Map(mapRef.current, {
            center: mapCenter,
            zoom: 13,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "on" }]
              }
            ]
          });

          console.log('✅ Map instance created successfully');

          // Add user location marker if available (using standard Marker)
          if (userLocation && window.google?.maps?.Marker) {
            userLocationMarkerRef.current = new window.google.maps.Marker({
              map: mapInstance,
              position: userLocation,
              title: 'Your Location',
              icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeColor: '#FFFFFF',
                strokeWeight: 2
              }
            });
            console.log('✅ User location marker added');
          }

          // Add click listener to map
          mapInstance.addListener('click', (event: any) => {
            if (event.latLng) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();
              
              // Mock address for demo (in real app, use reverse geocoding)
              const mockAddresses = [
                'New York, NY, USA',
                'Paris, France', 
                'Tokyo, Japan',
                'London, UK',
                'Sydney, Australia'
              ];
              
              const randomAddress = mockAddresses[Math.floor(Math.random() * mockAddresses.length)];
              
              const location = {
                lat: Math.round(lat * 10000) / 10000,
                lng: Math.round(lng * 10000) / 10000,
                address: randomAddress
              };
              
              setSelectedLocation(location);
              setTripDestination(location.address);

              // Add marker to map (using standard Marker)
              if (window.google?.maps?.Marker) {
                new window.google.maps.Marker({
                map: mapInstance,
                  position: { lat, lng },
                title: randomAddress
              });
              }
            }
          });

          setMap(mapInstance);
          
          // Store map instance on DOM element for access from other functions
          if (mapRef.current) {
            (mapRef.current as any).__googleMap = mapInstance;
          }
        } catch (error) {
          console.error('❌ Error initializing Google Map:', error);
          // Don't try to recover automatically on error - user can reload
        }
      } else if (window.google && window.google.maps && typeof window.google.maps.Map !== 'function') {
        // Google Maps is loaded but Map constructor is not available yet
        console.log('⚠️ Google Maps API loaded but Map constructor not ready yet...');
      }
    };

    // Check if Google Maps is already fully loaded
    // API is loaded globally, so just poll for it to be ready
    if (
      window.google && 
      window.google.maps && 
      typeof window.google.maps.Map === 'function'
    ) {
      initializeMap();
      } else {
      // Poll for Google Maps API to be loaded globally
      checkInterval = setInterval(() => {
        if (
          window.google && 
          window.google.maps && 
          typeof window.google.maps.Map === 'function'
        ) {
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
          initializeMap();
        }
      }, 100);
      
      // Timeout after 15 seconds to prevent infinite polling
      setTimeout(() => {
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
          console.warn('⚠️ Google Maps API loading timeout. Please check your API key and network connection.');
        }
      }, 15000);
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    };
  }, [hasApiKey, googleMapsApiKey, map, selectedLocation, userLocation, isGoogleMapsLoaded]);

  // Note: Demo location selection removed since we only show the map now

  const handlePlanTrip = () => {
    if (!tripDestination.trim()) {
      alert('Please enter a destination for your trip!');
      return;
    }
    
    // TODO: In a real app, this would save the trip plan to the backend
    alert(`Great! Planning your trip to ${tripDestination}. Activities: ${tripActivities || 'To be determined'}. Dates: ${tripDates || 'Flexible'}`);
  };

  const handleClearForm = () => {
    setTripDestination('');
    setTripActivities('');
    setTripDates('');
    setSelectedLocation(null);
  };

  // Fetch user conversations from API
  const fetchConversations = useCallback(async () => {
    const conversationsEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;
    
    if (!conversationsEndpoint) {
      console.warn('⚠️ REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT not configured');
      return;
    }
    
    if (!appSyncUser?.id) {
      console.warn('⚠️ User ID not available, cannot fetch conversations');
      return;
    }

    setIsLoadingConversations(true);
    setConversationsError(null);
    
    try {
      console.log('📡 Fetching conversations for user:', appSyncUser.id);
      
      // Get ID token from cache for Cognito authentication
      const { getTokensFromCache } = await import('../../../../services/auth/utils/tokenCache');
      const tokens = getTokensFromCache();
      
      if (!tokens?.idToken) {
        throw new Error('User not authenticated - no ID token found');
      }
      
      // Ensure the endpoint has /conversations in the path
      let endpoint = conversationsEndpoint;
      if (!endpoint.endsWith('/conversations')) {
        endpoint = endpoint.replace(/\/$/, '') + '/conversations';
      }
      
      console.log('🌐 Fetching from:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}` // Using Bearer + ID token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Conversations fetched:', data);
      
      // Assuming the API returns an array of conversations
      // Format: { conversations: [{conversation_id: string, timestamp: string}, ...] }
      const conversationsList = data.conversations || data || [];
      setConversations(conversationsList);
      
    } catch (error: any) {
      console.error('❌ Failed to fetch conversations:', error);
      setConversationsError(error.message || 'Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [appSyncUser?.id]);

  // Fetch a specific conversation's messages
  const fetchConversation = useCallback(async (conversationId: string) => {
    const conversationsEndpoint = process.env.REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT;
    
    if (!conversationsEndpoint) {
      console.warn('⚠️ REACT_APP_APIGATEWAY_CONVERSATIONS_ENDPOINT not configured');
      return;
    }

    try {
      console.log('📡 Fetching conversation:', conversationId);
      
      // Get ID token from cache for Cognito authentication
      const { getTokensFromCache } = await import('../../../../services/auth/utils/tokenCache');
      const tokens = getTokensFromCache();
      
      if (!tokens?.idToken) {
        throw new Error('User not authenticated - no ID token found');
      }
      
      // Build endpoint: /conversation?conversation_id=abc123
      let endpoint = conversationsEndpoint;
      if (endpoint.endsWith('/conversations')) {
        endpoint = endpoint.replace(/\/conversations$/, '/conversation');
      } else {
        endpoint = endpoint.replace(/\/$/, '') + '/conversation';
      }
      endpoint += `?conversation_id=${encodeURIComponent(conversationId)}`;
      
      console.log('🌐 Fetching from:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}` // Using Bearer + ID token
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversation: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Conversation fetched:', data);
      
      // Format the messages to match ChatMessage interface
      // API returns: { conversation_id, messages: [{message_id, created_at, text, actor}], count }
      const messages = data.messages || [];
      const formattedMessages: ChatMessage[] = messages.map((msg: any) => {
        const baseMessage = {
          id: msg.message_id?.toString() || `msg-${Date.now()}`,
          isUser: msg.actor === 'user',
          timestamp: new Date(msg.created_at * 1000), // Convert Unix timestamp to Date
          isLoading: false
        };

        // For assistant messages, parse the JSON to extract intent, trip_draft, etc.
        if (msg.actor === 'assistant' && msg.text) {
          const { response: agentResponse } = parseAgentResponse(msg.text);
          
          if (agentResponse) {
            // Successfully parsed agent response
            return {
              ...baseMessage,
              message: agentResponse.message,
              intent: agentResponse.intent,
              confidence: agentResponse.confidence,
              trip_draft: agentResponse.trip_draft,
              locations_recommendations: agentResponse.locations_recommendations
            } as ChatMessage;
          }
        }

        // For user messages or unparseable assistant messages, use text as-is
        return {
          ...baseMessage,
          message: msg.text || ''
        } as ChatMessage;
      });
      
      // Update chat messages with the loaded conversation
      setChatMessages(formattedMessages);
      
      // Scroll to bottom after loading messages
      setTimeout(() => {
        chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
    } catch (error: any) {
      console.error('❌ Failed to fetch conversation:', error);
      // Show error in chat
      setChatMessages([{
        id: `error-${Date.now()}`,
        message: `Failed to load conversation: ${error.message}`,
        isUser: false,
        timestamp: new Date(),
        isError: true
      }]);
    }
  }, [parseAgentResponse]);

  // Fetch conversations on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Helper function to search place by name when place_id is invalid or not found
  const searchPlaceByName = useCallback((placeName: string) => {
    if (!map) {
      console.warn('⚠️ Map not initialized for text search');
      setSelectedPlaceDetails({
        name: placeName,
        formatted_address: 'Map not ready',
        _isFallback: true
      });
      setIsLoadingPlaceDetails(false);
      return;
    }

    setIsLoadingPlaceDetails(true);
    console.log('🔍 Searching for place by name:', placeName);
    
    const service = new (window as any).google.maps.places.PlacesService(map);
    
    // Try to extract location context from recent chat messages
    let locationContext = '';
    const recentUserMessages = chatMessages
      .filter(msg => msg.isUser)
      .slice(-3); // Last 3 user messages
    
    for (const msg of recentUserMessages) {
      const text = msg.message.toLowerCase();
      // Look for common location patterns: "in [city]", "at [place]", "[city], [country]"
      const locationMatch = text.match(/\b(?:in|at|near)\s+([a-z\s,]+?)(?:\s|$|,)/i) || 
                           text.match(/\b([a-z]+,\s*[a-z]+)(?:\s|$)/i);
      if (locationMatch) {
        locationContext = locationMatch[1].trim();
        console.log('📍 Extracted location context from chat:', locationContext);
        break;
      }
    }
    
    // Build search query with location context
    const searchQuery = locationContext 
      ? `${placeName} in ${locationContext}`
      : placeName;
    
    // Get the current map center to bias the search to the visible area
    const mapCenter = map.getCenter();
    
    const textSearchRequest = {
      query: searchQuery,
      fields: ['name', 'formatted_address', 'formatted_phone_number', 'opening_hours', 'photos', 'rating', 'user_ratings_total', 'website', 'geometry', 'types', 'price_level', 'place_id'],
      // Bias search to current map location
      locationBias: mapCenter ? {
        center: mapCenter,
        radius: 50000 // 50km radius
      } : undefined
    };
    
    console.log('🔍 Final search query:', searchQuery);
    console.log('🗺️ Searching with location bias:', mapCenter?.toString());
    
    service.findPlaceFromQuery(textSearchRequest, (results: any, searchStatus: any) => {
      console.log('🔍 Text search results:', results, 'Status:', searchStatus);
      
      if (searchStatus === (window as any).google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        const foundPlace = results[0];
        console.log('✅ Found place by name search:', foundPlace);
        
        // Now get full details using the new place_id
        if (foundPlace.place_id) {
          const detailsRequest = {
            placeId: foundPlace.place_id,
            fields: ['name', 'formatted_address', 'formatted_phone_number', 'opening_hours', 'photos', 'rating', 'user_ratings_total', 'website', 'geometry', 'types', 'price_level']
          };
          
          service.getDetails(detailsRequest, (detailedPlace: any, detailStatus: any) => {
            if (detailStatus === (window as any).google.maps.places.PlacesServiceStatus.OK && detailedPlace) {
              console.log('✅ Got full details from text search:', detailedPlace);
              setSelectedPlaceDetails(detailedPlace);
              setLastSelectedPlaceId(foundPlace.place_id);
              
              // Center map on the location
              if (detailedPlace.geometry?.location) {
                map.setCenter(detailedPlace.geometry.location);
                map.setZoom(16);
                
                // Remove old marker if exists
                if (selectedPlaceMarkerRef.current) {
                  selectedPlaceMarkerRef.current.setMap(null);
                }
                
                // Add new marker
                if ((window as any).google?.maps?.Marker) {
                  selectedPlaceMarkerRef.current = new (window as any).google.maps.Marker({
                    map: map,
                    position: detailedPlace.geometry.location,
                    title: detailedPlace.name,
                    animation: (window as any).google.maps.Animation.DROP,
                    icon: {
                      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                        '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="24">📍</text></svg>'
                      ),
                      scaledSize: new (window as any).google.maps.Size(32, 32)
                    }
                  });
                }
              }
              setIsLoadingPlaceDetails(false);
            } else {
              console.error('❌ Failed to get details from text search result');
              setLastSelectedPlaceId(null);
              setSelectedPlaceDetails({
                name: placeName,
                formatted_address: 'Details unavailable',
                _isFallback: true
              });
              setIsLoadingPlaceDetails(false);
            }
          });
        }
      } else {
        console.error('❌ Text search failed');
        setLastSelectedPlaceId(null);
        setSelectedPlaceDetails({
          name: placeName,
          formatted_address: 'Place not found',
          _isFallback: true
        });
        setIsLoadingPlaceDetails(false);
      }
    });
  }, [map, chatMessages, setSelectedPlaceDetails, setLastSelectedPlaceId, setIsLoadingPlaceDetails, selectedPlaceMarkerRef]);

  // Fetch and display place details on the map
  const fetchAndDisplayPlaceDetails = useCallback(async (placeId: string, placeName: string, retryCount = 0) => {
    console.log('🔍 fetchAndDisplayPlaceDetails called');
    console.log('  📌 placeName:', placeName);
    console.log('  📌 placeId:', placeId);
    console.log('  📌 placeId type:', typeof placeId);
    console.log('  📌 placeId length:', placeId?.length);
    console.log('  📌 retryCount:', retryCount);
    
    // Check if place_id looks invalid (contains test patterns or suspicious repetitions)
    const isInvalidPlaceId = placeId && (
      placeId.includes('—-x—') || 
      placeId.includes('--x--') || 
      placeId.includes('-x-x-') ||
      /([A-Z])\1{4,}/.test(placeId) || // 5+ repeated letters like VVVVV
      /([a-z]{2,})\1{2,}/.test(placeId) || // Repeated patterns like GaGaGaGa
      placeId.match(/[^A-Za-z0-9_-]/) // Contains non-standard characters
    );
    
    if (isInvalidPlaceId) {
      console.warn('⚠️ Detected invalid/placeholder place_id, skipping to text search:', placeId);
      // Jump directly to text search by name
      searchPlaceByName(placeName);
      return;
    }
    
    // Increment request counter to track this request
    placeRequestCounterRef.current += 1;
    const thisRequestId = placeRequestCounterRef.current;
    console.log(`🔢 Place request ID: ${thisRequestId} for ${placeName}`);
    
    if (!map) {
      console.warn('⚠️ Map not initialized yet, cannot fetch place details');
      return;
    }

    setIsLoadingPlaceDetails(true);
    
    // Check if Places API is available
    let hasPlacesAPI = !!(window as any).google?.maps?.places?.PlacesService;
    console.log('🔍 Places API available:', hasPlacesAPI);
    
    // If Places API isn't loaded, try to load it dynamically using importLibrary (new method)
    if (!hasPlacesAPI && (window as any).google?.maps?.importLibrary) {
      console.log('🔄 Attempting to dynamically load Places library...');
      try {
        await (window as any).google.maps.importLibrary('places');
        hasPlacesAPI = !!(window as any).google?.maps?.places?.PlacesService;
        console.log('✅ Places library loaded dynamically:', hasPlacesAPI);
      } catch (error) {
        console.error('❌ Failed to load Places library dynamically:', error);
      }
    }
    
    if (!hasPlacesAPI) {
      console.warn('⚠️ Google Maps Places API not loaded yet, retry:', retryCount);
      
      // Max 5 retries
      if (retryCount < 5) {
        setSelectedPlaceDetails({
          name: placeName,
          formatted_address: 'Loading Places API...',
          _isFallback: true
        });
        setIsLoadingPlaceDetails(false);
        
        // Retry after a short delay
        setTimeout(() => {
          console.log('🔄 Retrying place details fetch (attempt', retryCount + 1, ')...');
          fetchAndDisplayPlaceDetails(placeId, placeName, retryCount + 1);
        }, 500);
        return;
      } else {
        console.error('❌ Failed to load Places API after 5 retries');
        setSelectedPlaceDetails({
          name: placeName,
          formatted_address: 'Google Maps API not loaded',
          _isFallback: true
        });
        setIsLoadingPlaceDetails(false);
        return;
      }
    }
    
    try {
      const service = new (window as any).google.maps.places.PlacesService(map);
        
        const request = {
          placeId: placeId,
          fields: ['name', 'formatted_address', 'formatted_phone_number', 'opening_hours', 'photos', 'rating', 'user_ratings_total', 'website', 'geometry', 'types', 'price_level']
        };
        
        console.log('📡 Calling Places API with request:', request);
        
        service.getDetails(request, (place: any, status: any) => {
          console.log('📥 Places API response - Status:', status, 'for request ID:', thisRequestId);
          
          // Check if this is still the latest request
          if (thisRequestId !== placeRequestCounterRef.current) {
            console.log(`⏭️ Ignoring stale response for ${placeName} (request ${thisRequestId}), current request is ${placeRequestCounterRef.current}`);
            return;
          }
          
          if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place) {
            console.log('✅ Place details fetched successfully:', place);
            setSelectedPlaceDetails(place);
            // Keep a compact copy for the agent (sent as context on the next message).
            selectedPlaceRef.current = normalizeSelectedPlace(place, placeId);
            
            // Mark this place as successfully selected
            setLastSelectedPlaceId(placeId);
            
            // Center map on the selected location
            if (place.geometry?.location) {
              console.log('📍 Centering map on:', place.geometry.location.toString());
              map.setCenter(place.geometry.location);
              map.setZoom(16);
              
              // Remove old marker if exists
              if (selectedPlaceMarkerRef.current) {
                console.log('🗑️ Removing old marker');
                selectedPlaceMarkerRef.current.setMap(null);
                selectedPlaceMarkerRef.current = null;
              }
              
              // Add marker for the selected place using standard Marker
              if ((window as any).google?.maps?.Marker) {
                console.log('📌 Adding marker to map');
                
                selectedPlaceMarkerRef.current = new (window as any).google.maps.Marker({
                  map: map,
                  position: place.geometry.location,
                  title: place.name,
                  animation: (window as any).google.maps.Animation.DROP,
                  icon: {
                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                      '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><text x="50%" y="50%" text-anchor="middle" dy=".3em" font-size="24">📍</text></svg>'
                    ),
                    scaledSize: new (window as any).google.maps.Size(32, 32)
                  }
                });
                
                console.log('✅ Place marker added successfully');
              }
            }
            setIsLoadingPlaceDetails(false);
          } else {
            // Map status to human-readable error
            const PlacesStatus = (window as any).google.maps.places.PlacesServiceStatus;
            let statusName = 'UNKNOWN';
            for (const [name, value] of Object.entries(PlacesStatus)) {
              if (value === status) {
                statusName = name;
                break;
              }
            }
            console.error('❌ Failed to fetch place details:', statusName, 'for', placeName, '(place_id:', placeId + ')');
            
            // Check if this is still the latest request
            if (thisRequestId !== placeRequestCounterRef.current) {
              console.log(`⏭️ Ignoring stale error for ${placeName} (request ${thisRequestId})`);
              return;
            }
            
            // If place_id is not found or invalid, try searching by name as fallback
            if (statusName === 'NOT_FOUND' || statusName === 'INVALID_REQUEST') {
              console.log('🔄 Place ID not found/invalid, trying text search by name:', placeName);
              searchPlaceByName(placeName);
            } else {
              // For other errors, just show error message
              setLastSelectedPlaceId(null);
              setSelectedPlaceDetails(null);
              setSelectedPlaceDetails({
                name: placeName,
                formatted_address: `Details unavailable (${statusName})`,
                _isFallback: true
              });
              setIsLoadingPlaceDetails(false);
            }
          }
        });
    } catch (error) {
      console.error('❌ Error fetching place details:', error);
      
      // Check if this is still the latest request
      if (thisRequestId !== placeRequestCounterRef.current) {
        console.log(`⏭️ Ignoring stale error for ${placeName} (request ${thisRequestId})`);
        return;
      }
      
      // Clear selection since fetch failed
      setLastSelectedPlaceId(null);
      
      setSelectedPlaceDetails({
        name: placeName,
        formatted_address: 'Error loading details',
        _isFallback: true
      });
      setIsLoadingPlaceDetails(false);
    }
  }, [map, searchPlaceByName]);

  // Store fetch function in ref so it can be called from handleLocationRecommendationSelect
  useEffect(() => {
    fetchPlaceDetailsRef.current = fetchAndDisplayPlaceDetails;
  }, [fetchAndDisplayPlaceDetails]);

  // Handle sending chat message
  const handleSendMessage = async (message: string, inputId: string) => {
    if (!message.trim() || isWaitingForResponse) {
      return;
    }

    // Check if connection is ready
    // Note: connectionId might not be immediately available but WebSocket is connected
    // For API Gateway WebSocket, connectionId is managed server-side and doesn't block sending messages
    if (!isConnected) {
      console.log('⏳ WebSocket not connected yet, please wait...');
      console.log('Connection status:', { isConnected, isConnecting, connectionId });
      // Show user-friendly message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        message: '⏳ Please wait for the connection to be established...',
        isUser: false,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
      return;
    }

    // Use test conversation_id if provided (for testing), otherwise use the one from agent
    // For the first message, conversationId will be null - do NOT send conversation_id to backend
    // Backend will create it and return it in the response
    const convId = testConversationId.trim() || conversationId || undefined;
    
    // Log conversation ID usage
    if (convId) {
      console.log('💬 Sending message with conversation ID:', convId);
    } else {
      console.log('💬 Sending new conversation message (no conversation_id - backend will create it)');
    }

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      message: message.trim(),
      isUser: true,
      timestamp: new Date()
    };

    // Add loading indicator
    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      message: '',
      isUser: false,
      timestamp: new Date(),
      isLoading: true
    };

    setChatMessages(prev => [...prev, userMessage, loadingMessage]);
    setCurrentInput('');
      setIsWaitingForResponse(true);

        // Set up timeout for 60 seconds (1 minute)
    // Clear any existing timeout first
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    
    responseTimeoutRef.current = setTimeout(() => {
      console.warn('⏱️ No response received after 20 seconds, timing out...');
      
      // Check if we're still waiting for a response
      setChatMessages(prev => {
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        
        // If there's a loading message, replace it with timeout error
        if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            message: 'Error: No response received from the agent. Please try again.',
            isLoading: false
          };
        } else {
          // If no loading message, add error message
          updated.push({
            id: `timeout-error-${Date.now()}`,
            message: 'Error: No response received from the agent. Please try again.',
            isUser: false,
            timestamp: new Date(),
            isLoading: false
          });
        }
        return updated;
      });
      
      // Unblock the input
      setIsWaitingForResponse(false);
      responseTimeoutRef.current = null;
      
      // Focus on input
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
      }, 60000); // 1 minute // 20 seconds

    try {
      // Prepare additional data: user_location and user_time
      const additionalData: { user_location?: { lat: string; lng: string }; user_time?: string; trip_draft?: TripDraft; selected_place?: Record<string, any> } = {};
      
      // Add user_location if available (convert numbers to strings)
      if (userLocation) {
        additionalData.user_location = {
          lat: userLocation.lat.toString(),
          lng: userLocation.lng.toString()
        };
      }
      
      // Add user_time as ISO string (current device time)
      additionalData.user_time = new Date().toISOString();

      // Include the current trip draft so the agent can modify it in place
      if (latestTripDraftRef.current) {
        additionalData.trip_draft = latestTripDraftRef.current;
      }

      // Include the place the user is currently viewing (details already fetched
      // by the map) so the agent can answer about it without a second lookup.
      if (selectedPlaceRef.current) {
        additionalData.selected_place = selectedPlaceRef.current;
      }

      // Send message with our local conversation_id and additional data
      await sendWebSocketMessage(message.trim(), convId, additionalData);
    } catch (error: any) {
      console.error('❌ Failed to send message:', error);
      
      // Clear timeout since we got an error
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
      }
      
      // Remove loading indicator and show error
      setChatMessages(prev => {
        const updated = [...prev];
        const lastMessageIndex = updated.length - 1;
        if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
          updated[lastMessageIndex] = {
            ...updated[lastMessageIndex],
            message: `Error: ${error.message || 'Failed to send message'}`,
            isLoading: false
          };
        }
        return updated;
      });
      setIsWaitingForResponse(false);
    }
  };

  // Handle input key press
  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, inputId: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const input = e.currentTarget;
      handleSendMessage(input.value, inputId);
    }
  };

  // Focus on input after response is received
  useEffect(() => {
    if (!isWaitingForResponse && isConnected) {
      setTimeout(() => {
        const inputElement = inputRefs.current['current-input'];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }, [isWaitingForResponse, isConnected]);

  // Photo lightbox handlers
  const openLightbox = (photoIndex: number) => {
    setLightboxPhotoIndex(photoIndex);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const goToPreviousPhoto = () => {
    if (selectedPlaceDetails?.photos) {
      const totalPhotos = Math.min(selectedPlaceDetails.photos.length, 8);
      setLightboxPhotoIndex((prev) => (prev > 0 ? prev - 1 : totalPhotos - 1));
    }
  };

  const goToNextPhoto = () => {
    if (selectedPlaceDetails?.photos) {
      const totalPhotos = Math.min(selectedPlaceDetails.photos.length, 8);
      setLightboxPhotoIndex((prev) => (prev < totalPhotos - 1 ? prev + 1 : 0));
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLightboxOpen) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          goToPreviousPhoto();
        } else if (e.key === 'ArrowRight') {
          goToNextPhoto();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedPlaceDetails?.photos]);

  return (
    <div className="plan-content">
      <div className="plan-trip-container">
        {/* Left Side - Trip Planning Form */}
        <div className="trip-planning-form">
          <div className="plan-header">
            <h2>✈️ Let's Plan Your Next Adventure!</h2>
            <p className="plan-subtitle">
              Tell us where you want to go and what amazing experiences you're dreaming of! 
              We'll help you create the perfect itinerary for your next unforgettable journey! 🌟
            </p>
          </div>

            {/* Conversations History */}
            <div className="conversations-history">
              <div 
                className="conversations-header"
                onClick={() => setIsConversationsDropdownOpen(!isConversationsDropdownOpen)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="dropdown-arrow" style={{ 
                    transform: isConversationsDropdownOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                    fontSize: '12px'
                  }}>
                    ▶
                  </span>
                  <h4>📚 Previous Conversations</h4>
                  {conversations.length > 0 && (
                    <span className="conversations-count">({conversations.length})</span>
                  )}
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchConversations();
                  }} 
                  disabled={isLoadingConversations}
                  className="refresh-conversations-btn"
                  title="Refresh conversations"
                >
                  {isLoadingConversations ? '⏳' : '🔄'}
                </button>
              </div>
              
              {/* Dropdown content - only show when open */}
              {isConversationsDropdownOpen && (
                <div className="conversations-dropdown-content">
                  {isLoadingConversations && (
                    <div className="conversations-loading">
                      <div className="loading-spinner-small"></div>
                      <span>Loading conversations...</span>
                    </div>
                  )}
                  
                  {conversationsError && (
                    <div className="conversations-error">
                      ⚠️ {conversationsError}
                    </div>
                  )}
                  
                  {!isLoadingConversations && !conversationsError && conversations.length === 0 && (
                    <div className="conversations-empty">
                      No previous conversations found. Start a new conversation below! 👇
                    </div>
                  )}
                  
                  {!isLoadingConversations && conversations.length > 0 && (
                    <div className="conversations-list">
                      {conversations.map((conv) => (
                        <div 
                          key={conv.conversation_id} 
                          className={`conversation-item ${conversationId === conv.conversation_id ? 'active' : ''}`}
                          onClick={() => {
                            console.log('🔄 Loading conversation:', conv.conversation_id);
                            setConversationId(conv.conversation_id);
                            setTestConversationId(conv.conversation_id);
                            // Clear created trip IDs when switching conversations
                            setCreatedTripMessageIds(new Set());
                            // Load conversation messages
                            fetchConversation(conv.conversation_id);
                          }}
                        >
                          <div className="conversation-preview">
                            <span className="preview-icon">{conv.actor === 'user' ? '👤' : '🤖'}</span>
                            <span className="preview-text">{conv.preview}</span>
                          </div>
                          <div className="conversation-timestamp">
                            <span className="timestamp-icon">🕐</span>
                            <span className="timestamp-value">
                              {new Date(conv.created_at * 1000).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* WebSocket Chat Interface */}
          <div className="websocket-chat-container" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="chat-header">
              <h3>💬 Trip Planning Assistant</h3>
              <div className="connection-status">
                {isConnecting && <span className="status-connecting">🟡 Connecting...</span>}
                {isConnected && <span className="status-connected">🟢 Connected</span>}
                {!isConnected && !isConnecting && <span className="status-disconnected">🔴 Disconnected</span>}
                {wsError && <span className="status-error">⚠️ {wsError}</span>}
              </div>
            </div>

            <div className="chat-messages" ref={chatMessagesContainerRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {chatMessages.length === 0 && (
                <div className="chat-welcome">
                  <p>Start a conversation about your trip! Ask me anything about planning your adventure. 🌟</p>
                </div>
              )}
              
              {chatMessages.map((msg, index) => (
                <div key={msg.id} className={`chat-message ${msg.isUser ? 'user-message' : 'assistant-message'}`}>
                  {msg.isLoading ? (
                    <div className="loading-dots">
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </div>
                  ) : (
                    <>
                      <div className="message-content">
                        {msg.message}
            </div>

                      {/* Error message styling */}
                      {msg.isError && (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          color: '#991b1b',
                          fontSize: '14px'
                        }}>
                          ⚠️ {msg.message}
            </div>
                      )}

                      {/* Location recommendations (for SUGGEST_RECOMMENDATIONS intent) */}
                      {msg.intent === 'SUGGEST_RECOMMENDATIONS' && msg.locations_recommendations && msg.locations_recommendations.length > 0 && (
                        <div className="location-recommendations" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {msg.locations_recommendations.map((location, idx) => {
                            // Only mark as selected if both place_id values are truthy and match
                            const isSelected = !!(lastSelectedPlaceId && location.place_id && lastSelectedPlaceId === location.place_id);
                            return (
                            <button
                              key={`${msg.id}-${location.place_id || location.name}-${idx}`}
                              onClick={() => handleLocationRecommendationSelect(location.name, msg.id, location.place_id, location)}
                              style={{
                                padding: '10px 16px',
                                borderRadius: '8px',
                                border: isSelected
                                  ? '2px solid #667eea'
                                  : '2px solid #e2e8f0',
                                background: isSelected
                                  ? 'rgba(102, 126, 234, 0.1)'
                                  : '#ffffff',
                                color: isSelected
                                  ? '#667eea'
                                  : '#1e293b',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: isSelected ? '600' : '500',
                                textAlign: 'left',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = '#f8fafc';
                                  e.currentTarget.style.borderColor = '#667eea';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.background = '#ffffff';
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                }
                              }}
                            >
                              <span style={{ fontWeight: '600' }}>
                                {location.name}
                                {isSelected && ' ✓'}
                              </span>
                            </button>
                            );
                          })}
            </div>
                      )}

                      {/* Trip draft form (for CONFIRM_TRIP intent) */}
                      {msg.intent === 'CONFIRM_TRIP' && msg.trip_draft && (
                        <>
                        <ConfirmTripForm
                          tripDraft={msg.trip_draft}
                          disabled={createdTripMessageIds.has(msg.id)}
                          onConfirm={async (updatedDraft) => {
                            try {
                            console.log('✅ Trip confirmed:', updatedDraft);
                              
                              // Validate required fields
                              if (!updatedDraft.destinations || updatedDraft.destinations.length === 0) {
                                alert('Please add at least one destination');
                                return;
                              }
                              
                              if (!updatedDraft.dates) {
                                alert('Please select start and end dates');
                                return;
                              }
                              
                              // Convert dates from MM-DD-YYYY to YYYY-MM-DD for backend
                              const convertToBackendDate = (mmddyyyy: string): string => {
                                const parts = mmddyyyy.split('-');
                                if (parts.length === 3) {
                                  // MM-DD-YYYY -> YYYY-MM-DD
                                  return `${parts[2]}-${parts[0]}-${parts[1]}`;
                                }
                                return mmddyyyy;
                              };
                              
                              // Convert TripDraft to CreateTripRequest format
                              const tripData = {
                                title: updatedDraft.title || updatedDraft.destinations.join(', '),
                                destinations: updatedDraft.destinations,
                                dates: {
                                  type: 'concrete' as const,
                                  startDate: convertToBackendDate(updatedDraft.dates.startDate),
                                  endDate: convertToBackendDate(updatedDraft.dates.endDate)
                                },
                                status: 'planning' as const, // Backend accepts: draft, planning, in_progress, completed
                                budget: updatedDraft.budget ? {
                                  amount: updatedDraft.budget.amount,
                                  currency: updatedDraft.budget.currency
                                } : undefined,
                                description: updatedDraft.description || undefined,
                                conversation_id: conversationId || undefined
                              };
                              
                              console.log('📤 Creating trip:', tripData);
                              
                              // Call createTrip API
                              const createdTrip = await createTrip(tripData);
                              
                              console.log('✅ Trip created successfully:', createdTrip);
                              
                              // Mark this message as having a created trip (to disable the form)
                              setCreatedTripMessageIds(prev => new Set(prev).add(msg.id));
                              
                              // Show success message with button to notify assistant
                              const successMessage: ChatMessage = {
                                id: `success-${Date.now()}`,
                                message: `🎉 Your trip has been created successfully!`,
                                isUser: false,
                                timestamp: new Date()
                              };
                              setChatMessages(prev => [...prev, successMessage]);
                              
                            } catch (error: any) {
                              console.error('❌ Failed to create trip:', error);
                              
                              // Show error message
                              const errorMessage: ChatMessage = {
                                id: `error-${Date.now()}`,
                                message: `❌ Failed to create trip: ${error.message || 'Unknown error'}`,
                                isUser: false,
                                timestamp: new Date(),
                                isError: true
                              };
                              setChatMessages(prev => [...prev, errorMessage]);
                            }
                          }}
                        />
                        
                        {/* Button to notify assistant after trip is created */}
                        {createdTripMessageIds.has(msg.id) && (
                          <button
                            onClick={async () => {
                              try {
                                // Send message to assistant
                                await sendWebSocketMessage('Trip confirmed', conversationId || undefined, {
                                  user_location: userLocation ? {
                                    lat: userLocation.lat.toString(),
                                    lng: userLocation.lng.toString()
                                  } : undefined,
                                  user_time: new Date().toISOString(),
                                  // The confirmed card's own draft is the authoritative one here
                                  trip_draft: msg.trip_draft || latestTripDraftRef.current || undefined
                                });
                                
                                // Add user message to chat
                                const userMsg: ChatMessage = {
                                  id: `user-${Date.now()}`,
                                  message: 'Trip confirmed',
                                  isUser: true,
                                  timestamp: new Date()
                                };
                                setChatMessages(prev => [...prev, userMsg]);
                                
                                // Show loading message
                                const loadingMsg: ChatMessage = {
                                  id: `loading-${Date.now()}`,
                                  message: '',
                                  isUser: false,
                                  timestamp: new Date(),
                                  isLoading: true
                                };
                                setChatMessages(prev => [...prev, loadingMsg]);
                                setIsWaitingForResponse(true);
                                
                              } catch (error: any) {
                                console.error('❌ Failed to send message:', error);
                                alert('Failed to notify assistant. Please try again.');
                              }
                            }}
                            style={{
                              marginTop: '12px',
                              padding: '12px 24px',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              width: '100%',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
                          >
                            ✅ Notify Assistant - Trip Confirmed
                          </button>
                        )}
                        </>
                      )}

                      {/* Location buttons (old format, kept for backwards compatibility) */}
                      {msg.locations && msg.locations.length > 0 && (
                        <div className="location-buttons" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {msg.locations.map((location) => (
              <button 
                              key={location.index}
                              onClick={() => handleLocationSelect(location.name, msg.id)}
                              disabled={!!msg.selectedLocation}
                              style={{
                                padding: '10px 16px',
                                borderRadius: '8px',
                                border: msg.selectedLocation === location.name 
                                  ? '2px solid #10b981' 
                                  : '2px solid #e2e8f0',
                                background: msg.selectedLocation === location.name
                                  ? '#ecfdf5'
                                  : '#ffffff',
                                color: msg.selectedLocation === location.name
                                  ? '#10b981'
                                  : '#1e293b',
                                cursor: msg.selectedLocation ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: msg.selectedLocation === location.name ? '600' : '500',
                                textAlign: 'left',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start'
                              }}
                              onMouseEnter={(e) => {
                                if (!msg.selectedLocation) {
                                  e.currentTarget.style.background = '#f8fafc';
                                  e.currentTarget.style.borderColor = '#3b82f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!msg.selectedLocation) {
                                  e.currentTarget.style.background = '#ffffff';
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                }
                              }}
                            >
                              <span style={{ fontWeight: '600', marginBottom: '4px' }}>
                                {location.index}. {location.name}
                                {msg.selectedLocation === location.name && ' ✓'}
                              </span>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>
                                📍 {location.address}
                              </span>
              </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Show selected location confirmation */}
                      {msg.selectedLocation && (
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '8px 12px', 
                          background: '#ecfdf5', 
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: '#10b981',
                          fontWeight: '500'
                        }}>
                          ✓ Selected: {msg.selectedLocation}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* Invisible element at the bottom to scroll to */}
              <div ref={chatMessagesEndRef} />
            </div>

            {/* Input field - allow typing even if not fully connected, but disable sending */}
            <div className="chat-input-wrapper">
              <input
                ref={(el) => {
                  if (el) inputRefs.current['current-input'] = el;
                }}
                id="current-input"
                type="text"
                placeholder={
                  isWaitingForResponse 
                    ? "Waiting for response..." 
                    : isConnected
                    ? "Type your message... (Press Enter to send)"
                    : isConnecting
                    ? "Connecting to chat... (Please wait)"
                    : "Connecting... (You can type your message)"
                }
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onKeyPress={(e) => handleInputKeyPress(e, 'current-input')}
                disabled={isWaitingForResponse}
                className={isWaitingForResponse ? "chat-input chat-input-disabled" : "chat-input"}
              />
              <button 
                onClick={() => {
                  const input = inputRefs.current['current-input'];
                  if (input) {
                    handleSendMessage(input.value, 'current-input');
                  }
                }}
                disabled={isWaitingForResponse || !currentInput.trim()}
                className={
                  (isWaitingForResponse || !currentInput.trim() || !isConnected) 
                    ? "chat-send-button chat-send-button-disabled" 
                    : "chat-send-button"
                }
                title={
                  !currentInput.trim()
                    ? "Enter a message to send"
                    : !isConnected
                    ? "Waiting for connection to be ready... (You can type, but message won't send until connected)"
                    : isWaitingForResponse
                    ? "Waiting for response..."
                    : "Send message"
                }
              >
                {!isConnected ? '⏳ Send' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Interactive Map */}
        <div>
          {hasApiKey ? (
            <div className="gmp-place-picker-container">
              <div style={{ position: 'relative', flexShrink: 0 }}>
              <div 
                ref={mapRef}
                className="google-map-container"
                style={{
                  height: '400px',
                  width: '100%',
                  borderRadius: '12px',
                  border: '2px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}
              />
                {/* Loading overlay - sibling, not child of map container */}
                {(!isGoogleMapsLoaded || !map) && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#1a1a1a',
                    borderRadius: '12px',
                    gap: '16px',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid #2a2a2a',
                      borderTop: '3px solid #667eea',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <div style={{
                      color: '#a1a1aa',
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      {!isGoogleMapsLoaded ? '📡 Loading Google Maps...' : '🗺️ Initializing map...'}
                    </div>
                  </div>
                )}
              </div>
              
                {/* Show location errors only */}
                {locationError && locationError !== 'Getting your location...' && (
                  <div className="location-error" style={{ 
                    marginTop: '12px', 
                    padding: '10px 16px', 
                    background: '#fef2f2', 
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#991b1b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>⚠️</span>
                    <span>{locationError}</span>
                  </div>
                )}
                  {selectedLocation && (
                    <div className="selected-place">
                      📍 Selected: {selectedLocation.address}
                      <br />
                      <small>Coordinates: {selectedLocation.lat}, {selectedLocation.lng}</small>
                    </div>
                  )}
                  
                  {/* Place Details Section */}
                  {isLoadingPlaceDetails && (
                    <div className="place-details-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading place details...</p>
                    </div>
                  )}
                  
                  {selectedPlaceDetails && !isLoadingPlaceDetails && (
                    <div className="place-details-card">
                      <div className="place-details-header">
                        <h3>
                          {selectedPlaceDetails.name}
                          {selectedPlaceDetails.rating && (
                            <span className="place-rating-inline">
                              <span className="rating-stars">⭐ {selectedPlaceDetails.rating}</span>
                              {selectedPlaceDetails.user_ratings_total && (
                                <span className="rating-count">({selectedPlaceDetails.user_ratings_total} reviews)</span>
                              )}
                            </span>
                          )}
                        </h3>
                      </div>
                      
                      {/* Photos Section */}
                      {selectedPlaceDetails.photos && selectedPlaceDetails.photos.length > 0 && (
                        <div className="place-photos-carousel">
                          {/* Navigation Arrow - Left */}
                          {selectedPlaceDetails.photos.length > 3 && photoStartIndex > 0 && (
                            <button 
                              className="photo-nav-arrow photo-nav-left"
                              onClick={() => setPhotoStartIndex(Math.max(0, photoStartIndex - 3))}
                              aria-label="Previous photos"
                            >
                              ‹
                            </button>
                          )}
                          
                          <div className="place-photos">
                            {selectedPlaceDetails.photos.slice(0, 8).slice(photoStartIndex, photoStartIndex + 3).map((photo: any, idx: number) => (
                              <div 
                                key={photoStartIndex + idx} 
                                className="place-photo"
                                onClick={() => openLightbox(photoStartIndex + idx)}
                                style={{ cursor: 'pointer' }}
                              >
                                <img 
                                  src={photo.getUrl({ maxWidth: 400, maxHeight: 300 })} 
                                  alt={`${selectedPlaceDetails.name} photo ${photoStartIndex + idx + 1}`}
                                />
                              </div>
                            ))}
                          </div>
                          
                          {/* Navigation Arrow - Right */}
                          {selectedPlaceDetails.photos.length > 3 && photoStartIndex + 3 < Math.min(selectedPlaceDetails.photos.length, 8) && (
                            <button 
                              className="photo-nav-arrow photo-nav-right"
                              onClick={() => setPhotoStartIndex(Math.min(Math.min(selectedPlaceDetails.photos.length, 8) - 3, photoStartIndex + 3))}
                              aria-label="Next photos"
                            >
                              ›
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* Place Information */}
                      <div className="place-info">
                        {/* Combined Contact Info Block */}
                        {(selectedPlaceDetails.formatted_address || selectedPlaceDetails.formatted_phone_number || selectedPlaceDetails.website || selectedPlaceDetails.opening_hours) && (
                          <div className="place-info-item">
                            <div className="info-text" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {selectedPlaceDetails.formatted_address && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <span className="info-icon">📍</span>
                                  <span>{selectedPlaceDetails.formatted_address}</span>
                                </div>
                              )}
                              
                              {selectedPlaceDetails.website && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <span className="info-icon">🌐</span>
                                  <span>
                                    Website - <a 
                                    href={selectedPlaceDetails.website} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="info-link"
                                    style={{ 
                                      fontSize: '13px',
                                      wordBreak: 'break-all',
                                      display: 'inline'
                                    }}
                                  >
                                    {selectedPlaceDetails.website}
                                  </a>
                                  </span>
                                </div>
                              )}
                              
                              {selectedPlaceDetails.opening_hours && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                  <span className="info-icon">🕐</span>
                                  <div style={{ flex: 1 }}>
                                    {selectedPlaceDetails.opening_hours.isOpen !== undefined && (
                                      <div style={{ marginBottom: '6px' }}>
                                        <strong style={{ 
                                          color: selectedPlaceDetails.opening_hours.isOpen() ? '#10b981' : '#ef4444',
                                          fontSize: '13px'
                                        }}>
                                          {selectedPlaceDetails.opening_hours.isOpen() ? '🟢 Open Now' : '🔴 Closed'}
                                        </strong>
                                      </div>
                                    )}
                                    {selectedPlaceDetails.opening_hours.weekday_text && (
                                      <div className="opening-hours">
                                        {selectedPlaceDetails.opening_hours.weekday_text.map((text: string, idx: number) => (
                                          <div key={idx} className="hours-row">{text}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
            </div>
          ) : (
            // Fallback for no API key
            <div className="gmp-place-picker-container">
              <div className="map-placeholder-gmp">
                <div className="gmp-map-info">
                  <h3>🗺️ Google Maps Integration</h3>
                  <p>Add your API key to enable live maps</p>
                  <div className="gmp-setup-instructions">
                    <h4>🔑 Setup Instructions:</h4>
                    <ol>
                      <li>Create <code>.env</code> file in project root</li>
                      <li>Restart server: <code>npm start</code></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Photo Lightbox */}
      {isLightboxOpen && selectedPlaceDetails?.photos && selectedPlaceDetails.photos.length > 0 && (
        <div className="photo-lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Previous arrow */}
            {selectedPlaceDetails.photos.slice(0, 8).length > 1 && (
              <button 
                className="lightbox-arrow lightbox-arrow-left"
                onClick={goToPreviousPhoto}
                aria-label="Previous photo"
              >
                ‹
              </button>
            )}
            
            {/* Photo */}
            <div className="lightbox-image-container">
              {/* Close button */}
              <button 
                className="lightbox-close" 
                onClick={closeLightbox}
                aria-label="Close lightbox"
              >
                ✕
              </button>
              
              <img 
                src={selectedPlaceDetails.photos.slice(0, 8)[lightboxPhotoIndex]?.getUrl({ maxWidth: 1200, maxHeight: 800 })} 
                alt={`${selectedPlaceDetails.name} photo ${lightboxPhotoIndex + 1}`}
                className="lightbox-image"
              />
              <div className="lightbox-counter">
                {lightboxPhotoIndex + 1} / {Math.min(selectedPlaceDetails.photos.length, 8)}
              </div>
            </div>
            
            {/* Next arrow */}
            {selectedPlaceDetails.photos.slice(0, 8).length > 1 && (
              <button 
                className="lightbox-arrow lightbox-arrow-right"
                onClick={goToNextPhoto}
                aria-label="Next photo"
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Plan;